import type { ExportScriptParagraphRole } from '@narrativebench/editor-core';

export const SCRIPT_DIALOGUE_INDENT_TWIPS = 2160;

export const SCRIPT_DIALOGUE_INSET_RATIO = 0.15;

export const SCRIPT_CHARACTER_CUE_TOP_GAP_EM = 1;

export const SCRIPT_SCENE_HEADING_VERTICAL_GAP_LH = 1;

export const scriptCharacterCueSpacingBeforeTwips = (fontSizePt: number): number =>
  Math.round(fontSizePt * 20);

export const scriptCharacterCueTopGapMm = (fontSizePt: number): number =>
  (fontSizePt * SCRIPT_CHARACTER_CUE_TOP_GAP_EM * 25.4) / 72;

export const scriptSceneHeadingSpacingTwips = (fontSizePt: number): number =>
  Math.round(fontSizePt * 20 * SCRIPT_SCENE_HEADING_VERTICAL_GAP_LH);

export const scriptSceneHeadingGapMm = (
  fontSizePt: number,
  lineHeightMm: number,
): number => lineHeightMm * SCRIPT_SCENE_HEADING_VERTICAL_GAP_LH;

type InlineExportNode = {
  type?: string;
  text?: string;
  attrs?: {
    label?: unknown;
    id?: unknown;
    mentionSuggestionChar?: unknown;
    entityType?: unknown;
  };
};

export const getMentionExportText = (node: InlineExportNode): string => {
  if (node.type !== 'mention' && node.type !== 'scriptMention') {
    return '';
  }
  const label = typeof node.attrs?.label === 'string' ? node.attrs.label.trim() : '';
  const id = typeof node.attrs?.id === 'string' ? node.attrs.id.trim() : '';
  const name = label || id;
  if (!name) {
    return '';
  }
  const suggestionChar =
    typeof node.attrs?.mentionSuggestionChar === 'string' && node.attrs.mentionSuggestionChar
      ? node.attrs.mentionSuggestionChar
      : '@';
  return `${suggestionChar}${name}`;
};

export const getInlineNodeExportText = (node: InlineExportNode): string => {
  if (node.type === 'text') {
    return node.text || '';
  }
  if (node.type === 'hardBreak') {
    return '\n';
  }
  return getMentionExportText(node);
};

export const shouldUppercaseScriptRole = (role: ExportScriptParagraphRole): boolean =>
  role === 'scene_heading' || role === 'character_cue';

export const applyScriptRoleTextTransform = (
  text: string,
  role: ExportScriptParagraphRole,
): string => {
  if (!shouldUppercaseScriptRole(role)) {
    return text;
  }
  return text.toLocaleUpperCase();
};

export const isScriptDialogueRole = (role: ExportScriptParagraphRole): boolean =>
  role === 'dialogue';

export const isScriptParentheticalRole = (role: ExportScriptParagraphRole): boolean =>
  role === 'parenthetical';

export const isScriptCharacterCueRole = (role: ExportScriptParagraphRole): boolean =>
  role === 'character_cue';

export const isScriptSceneHeadingRole = (role: ExportScriptParagraphRole): boolean =>
  role === 'scene_heading';
